import React, { useEffect, useState, useSyncExternalStore } from "react";
import {
  withLDProvider,
  useFlags,
  useLDClient,
  type LDContext,
  type LDMultiKindContext,
} from "launchdarkly-react-client-sdk";
import { useAuth } from "./authContext";
import { config } from "../config";

// Read through `config` so a value injected at container start (window.__ENV__)
// wins over the one Vite inlined at build time — otherwise every image, QA
// included, is permanently pinned to whichever LaunchDarkly environment was
// baked in at build. `|| undefined` keeps the unconfigured case identical to
// before: LaunchDarkly stays off and every flag reads its default.
const clientSideID: string | undefined = config.ldClientId || undefined;

/**
 * Anonymous bootstrap context used before the user has authenticated. Once auth
 * resolves, `LaunchDarklyIdentify` re-identifies with the real workspace/user
 * multi-context.
 */
const ANONYMOUS_CONTEXT: LDContext = {
  kind: "user",
  key: "anonymous",
  anonymous: true,
};

/**
 * Registry of LaunchDarkly flag keys used by the frontend.
 *
 * Add one entry per flag, with the value matching the key configured in the
 * LaunchDarkly dashboard exactly (kebab-case, shared with the backend
 * `FlagKey`). Remember to enable "Client-side SDK availability" on the flag, or
 * it will always read its default in the browser.
 */
export const FeatureFlag = {
  // auditorTrail: "auditor-trail",
  // Hybrid testing (analytics columns). Here it holds every authoring surface;
  // see `useHybridTestingAvailability`. The backend reads the same key to keep
  // out analytics content Midship wrote: its agent proposes and drafts none, a
  // plan edit that introduces a proposed analytics column is refused, and so is
  // generating or finalizing a plan that holds any. It also reads the key to
  // report a plan's untested attributes. No route is gated on the key, so a
  // person's own authoring is held here alone. The dashboard
  // flag is the project-level `hybrid-testing` flag; a key with no dashboard
  // flag reads its default (false) forever.
  hybridTesting: "hybrid-testing",
  // Analytics columns on work steps, rolled out separately from controls. A
  // work step needs both this and `hybridTesting`, here and on the backend
  // alike, and neither gates a work-step write route.
  workStepHybridTesting: "work-step-hybrid-testing",
  cortanaV2Executor: "cortana-v2-executor",
  liveDocumentProcessing: "live-document-processing",
  soc1ExtractionWorkpaper: "soc1-extraction-workpaper",
  optroBulkImport: "optro-bulk-import",
  // Gates Amplitude emission. Targeted per workspace so the rollout can go a
  // percentage at a time; read via `MetricsGate`, never in feature code.
  midshipAnalytics: "midship-analytics",
  signInWithOptro: "sign-in-with-optro",
  workStepsIntegration: "ops-audit-midship-integration",
  optroMidshipIntegration: "optro-midship-integration",
  // Generate work-paper values by dispatching to a worker and polling, instead
  // of holding one long request open. Frontend-only: the backend serves both
  // endpoints unconditionally.
  templateGenerationAsync: "control-template-gen-async",
  // Enforce Optro view access on Optro-linked controls and work steps; read
  // via `useRespectOptroPermissions`. The backend reads the same key.
  respectOptroPermissions: "respect-optro-permissions",
  // Offers the light/dark/system theme picker; read via `useDarkModeAvailability`.
  // Frontend-only.
  darkMode: "dark-mode",
} as const;

/**
 * Wrap a component tree with the LaunchDarkly provider.
 *
 * Flag keys are kept verbatim (kebab-case) to match the backend `FlagKey`
 * values rather than the SDK's default camelCasing. When no client-side ID is
 * configured, the component renders unwrapped and every flag falls back to its
 * default — fail-closed, mirroring the backend's offline path.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function withLaunchDarkly<P extends object>(
  Component: React.ComponentType<P>,
): React.ComponentType<P> {
  if (!clientSideID) {
    console.warn(
      "VITE_LAUNCHDARKLY_CLIENT_ID is not set; LaunchDarkly flags will use defaults.",
    );
    return Component;
  }

  return withLDProvider<P>({
    clientSideID,
    context: ANONYMOUS_CONTEXT,
    reactOptions: { useCamelCaseFlagKeys: false },
  })(Component);
}

/**
 * Syncs the LaunchDarkly evaluation context with the authenticated user and
 * workspace. Renders nothing; mount it once inside both the auth and
 * LaunchDarkly providers.
 *
 * Uses the same `workspace`/`user` context kinds and keys as the backend so a
 * targeting rule written once applies identically in the browser and the API.
 * That parity extends to the `sandbox` attribute: the backend stamps it from
 * `forge.sandboxes` (see `feature_flags.launchdarkly.build_context`), and we
 * pass through the value `GET /user/` hydrated onto the workspace. Without it a
 * "sandbox is true" rule silently matches nothing in the browser — a missing
 * attribute is not an error in LaunchDarkly, the rule just never fires.
 */
export function LaunchDarklyIdentify(): null {
  const ldClient = useLDClient();
  const { user, workspace } = useAuth();

  useEffect(() => {
    if (!user && !workspace) return;
    // Readiness must settle even when LaunchDarkly can't be reached: the
    // provider only hands over the client once its init settles, and a hung
    // init or identify would otherwise hold every flag-gated surface pending
    // forever. Past the deadline identification counts as failed (flags are
    // then not authoritative); a later success still upgrades it.
    const deadline = setTimeout(
      () => identifiedStore.failIfPending(),
      IDENTIFY_TIMEOUT_MS,
    );
    if (!ldClient) {
      return () => clearTimeout(deadline);
    }

    const context: LDMultiKindContext = { kind: "multi" };
    if (workspace) {
      context.workspace = {
        key: workspace.id,
        name: workspace.name,
        sandbox: workspace.sandbox,
      };
    }
    if (user) {
      context.user = { key: user.id, email: user.email };
    }

    void ldClient
      .identify(context)
      .then(
        () => identifiedStore.set("identified"),
        () => identifiedStore.failIfPending(),
      )
      .finally(() => clearTimeout(deadline));
    return () => clearTimeout(deadline);
  }, [ldClient, user, workspace]);

  return null;
}

type Identification = "pending" | "identified" | "failed";

// How long identification may take before it counts as failed. LaunchDarkly's
// own guidance for waiting on initialization is a few seconds.
const IDENTIFY_TIMEOUT_MS = 5000;

interface IdentifiedStore {
  identified: Identification;
  listeners: Set<() => void>;
  set(value: Identification): void;
  failIfPending(): void;
  subscribe(listener: () => void): () => void;
  getSnapshot(): Identification;
}

/**
 * Tracks whether `LaunchDarklyIdentify` has finished re-identifying the
 * client with the real workspace/user context — or given up on it. A
 * module-level store rather than React context because
 * `LaunchDarklyIdentify` renders as a sibling of the app, not around it.
 */
const identifiedStore: IdentifiedStore = {
  identified: "pending",
  listeners: new Set<() => void>(),
  set(value) {
    this.identified = value;
    this.listeners.forEach((listener) => listener());
  },
  // A failure never overrides a completed identify.
  failIfPending() {
    if (this.identified === "pending") this.set("failed");
  },
  subscribe(listener) {
    identifiedStore.listeners.add(listener);
    return () => {
      identifiedStore.listeners.delete(listener);
    };
  },
  getSnapshot() {
    return identifiedStore.identified;
  },
};

export type FeatureFlagsStatus = "pending" | "ready" | "failed";

/**
 * Where flag values stand for the signed-in workspace/user: "ready" =
 * authoritative (as `useFeatureFlagsReady`); "failed" = LaunchDarkly couldn't
 * be reached or identify failed/timed out, so flag values are defaults or the
 * anonymous context's and callers that must fail a certain way (e.g. treat a
 * flag as off) should do so explicitly.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useFeatureFlagsStatus(): FeatureFlagsStatus {
  const ldClient = useLDClient();
  const identified = useSyncExternalStore(
    identifiedStore.subscribe,
    identifiedStore.getSnapshot,
  );
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!ldClient || initialized) return;
    let cancelled = false;
    void ldClient.waitUntilReady().then(() => {
      if (!cancelled) setInitialized(true);
    });
    return () => {
      cancelled = true;
    };
  }, [ldClient, initialized]);

  // No LD configured: every flag reads its default forever, so the values
  // are as ready as they will ever be.
  if (!clientSideID) return "ready";
  if (identified === "failed") return "failed";
  return initialized && identified === "identified" ? "ready" : "pending";
}

/**
 * Whether flag values are authoritative for the signed-in workspace/user.
 *
 * `useFeatureFlag` returns its fail-closed default until the client's initial
 * fetch resolves, and then the ANONYMOUS context's values until the identify
 * with the real context completes — so a redirect keyed on a flag would fire
 * spuriously on a cold page load. Gate such redirects on this instead.
 * Only meaningful on authenticated surfaces (identification requires auth).
 *
 * Stays false if identification fails or times out; callers that must settle
 * either way read `useFeatureFlagsStatus` instead.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useFeatureFlagsReady(): boolean {
  return useFeatureFlagsStatus() === "ready";
}

/**
 * Read a boolean LaunchDarkly flag by key, returning `defaultValue` until flags
 * have loaded or when the key is unknown/non-boolean (fail-closed by default).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useFeatureFlag(flagKey: string, defaultValue = false): boolean {
  const flags = useFlags();
  const value = flags[flagKey];
  return typeof value === "boolean" ? value : defaultValue;
}
